// Radix-2 Cooley-Tukey FFT, in-place on real/imag Float32Arrays.
// Precomputed twiddle tables keep the inner loop branch-free and cache-friendly.

export class FFT {
  constructor(n) {
    if ((n & (n - 1)) !== 0) throw new Error(`FFT size must be power of 2 (got ${n})`);
    this.n = n;
    this.bitReverse = buildBitReverseTable(n);
    this.twiddleCos = new Float32Array(n / 2);
    this.twiddleSin = new Float32Array(n / 2);
    for (let i = 0; i < n / 2; i += 1) {
      const angle = (-2 * Math.PI * i) / n;
      this.twiddleCos[i] = Math.cos(angle);
      this.twiddleSin[i] = Math.sin(angle);
    }
  }

  forward(real, imag) {
    const { n, bitReverse, twiddleCos, twiddleSin } = this;

    for (let i = 0; i < n; i += 1) {
      const j = bitReverse[i];
      if (j > i) {
        swap(real, i, j);
        swap(imag, i, j);
      }
    }

    for (let size = 2; size <= n; size <<= 1) {
      const half = size >> 1;
      const step = n / size;
      for (let base = 0; base < n; base += size) {
        for (let k = 0, tw = 0; k < half; k += 1, tw += step) {
          const a = base + k;
          const b = a + half;
          const wr = twiddleCos[tw];
          const wi = twiddleSin[tw];
          const tr = wr * real[b] - wi * imag[b];
          const ti = wr * imag[b] + wi * real[b];
          real[b] = real[a] - tr;
          imag[b] = imag[a] - ti;
          real[a] += tr;
          imag[a] += ti;
        }
      }
    }
  }
}

function swap(arr, i, j) {
  const tmp = arr[i];
  arr[i] = arr[j];
  arr[j] = tmp;
}

function buildBitReverseTable(n) {
  const bits = Math.log2(n);
  const table = new Uint32Array(n);
  for (let i = 0; i < n; i += 1) {
    let j = 0;
    for (let b = 0; b < bits; b += 1) {
      j = (j << 1) | ((i >> b) & 1);
    }
    table[i] = j;
  }
  return table;
}
